import React, { Suspense, lazy, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Seo from "../components/Seo";
import { SectionLoading } from "../components/admin/shared";

// Each section is its own chunk, fetched only when its tab is first opened.
// Radix mounts just the active TabsContent, so a section's data request and
// its JavaScript both arrive on demand instead of all thirteen up front.
const TrainersSection = lazy(() => import("../components/admin/TrainersSection"));
const KycSection = lazy(() => import("../components/admin/KycSection"));
const UsersSection = lazy(() => import("../components/admin/UsersSection"));
const BookingsSection = lazy(() => import("../components/admin/BookingsSection"));
const PayoutsSection = lazy(() => import("../components/admin/PayoutsSection"));
const ZonesSection = lazy(() => import("../components/admin/ZonesSection"));
const TaxesSection = lazy(() => import("../components/admin/TaxesSection"));
const RulesSection = lazy(() => import("../components/admin/RulesSection"));
const SubAdminsSection = lazy(() => import("../components/admin/SubAdminsSection"));
const DataSection = lazy(() => import("../components/admin/DataSection"));
const AdminSessions = lazy(() => import("../components/AdminSessions"));
const AdminPerformance = lazy(() => import("../components/AdminPerformance"));

function Section({ children }) {
  return <Suspense fallback={<SectionLoading />}>{children}</Suspense>;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const can = (p) => isAdmin || (user?.permissions || []).includes(p);

  // The shell only needs the headline counters; every tab loads its own data.
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/admin/stats")
      .then(r => { if (alive) setStats(r.data); })
      .catch(() => { if (alive) setStats(false); });
    return () => { alive = false; };
  }, [user?.user_id]);

  // First section this account can actually open, so a sub-admin never lands
  // on an empty panel.
  const defaultTab = [
    ["trainers", "trainers.approve"],
    ["kyc", "kyc.review"],
    ["bookings", "bookings.view"],
    ["bookings", "disputes.manage"],
    ["users", "users.view"],
    ["payouts", "payouts.view"],
    ["zones", "zones.manage"],
    ["performance", "performance.view"],
    ["sessions", "sessions.view"],
  ].find(([, perm]) => can(perm))?.[0] || "trainers";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="admin-dashboard">
      <Seo title="Admin Console – DriveTerrain" description="DriveTerrain administrator console." canonical="/admin" robots="noindex, nofollow" hrefLangs={false} />
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary">Admin Console</div>
        <h1 className="text-4xl font-display font-semibold tracking-tight mt-1">Platform overview</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-8">
        {[
          { l: "Learners", v: stats?.learners },
          { l: "Trainers", v: stats?.trainers },
          { l: "Bookings", v: stats?.bookings },
          { l: "Paid", v: stats?.paid_bookings },
          // Revenue is only returned to accounts allowed to see financials.
          ...(stats && stats.revenue !== undefined
            ? [{ l: "Revenue", v: `$${stats.revenue.toFixed(2)}` }] : []),
        ].map((s, i) => (
          <div key={s.l} className="border border-border bg-card p-4 sm:p-5 min-w-0" data-testid={`stat-${i}`}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground truncate">{s.l}</div>
            <div className="font-display text-2xl sm:text-3xl font-semibold mt-1 truncate">
              {s.v === undefined || s.v === null ? <span className="text-muted-foreground">—</span> : s.v}
            </div>
          </div>
        ))}
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="rounded-sm flex-nowrap md:flex-wrap h-auto max-w-full overflow-x-auto no-scrollbar justify-start">
          {can("trainers.approve") && <TabsTrigger value="trainers" data-testid="tab-trainers">Trainer Approvals</TabsTrigger>}
          {can("kyc.review") && <TabsTrigger value="kyc" data-testid="tab-kyc">KYC Queue</TabsTrigger>}
          {can("users.view") && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
          {(can("bookings.view") || can("disputes.manage")) && <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>}
          {can("payouts.view") && <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>}
          {can("zones.manage") && <TabsTrigger value="zones" data-testid="tab-zones">Zones</TabsTrigger>}
          {isAdmin && <TabsTrigger value="taxes" data-testid="tab-taxes">Taxes</TabsTrigger>}
          {isAdmin && <TabsTrigger value="rules" data-testid="tab-rules">Business Rules</TabsTrigger>}
          {isAdmin && <TabsTrigger value="subadmins" data-testid="tab-subadmins">Sub-Admins</TabsTrigger>}
          {can("sessions.view") && <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>}
          {can("performance.view") && <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>}
          {isAdmin && <TabsTrigger value="data" data-testid="tab-data">Data</TabsTrigger>}
        </TabsList>

        {can("trainers.approve") && <TabsContent value="trainers" className="mt-6"><Section><TrainersSection /></Section></TabsContent>}
        {can("kyc.review") && <TabsContent value="kyc" className="mt-6"><Section><KycSection /></Section></TabsContent>}
        {can("users.view") && <TabsContent value="users" className="mt-6"><Section><UsersSection /></Section></TabsContent>}
        {(can("bookings.view") || can("disputes.manage")) && (
          <TabsContent value="bookings" className="mt-6">
            <Section><BookingsSection isAdmin={isAdmin} can={can} /></Section>
          </TabsContent>
        )}
        {can("payouts.view") && <TabsContent value="payouts" className="mt-6"><Section><PayoutsSection isAdmin={isAdmin} /></Section></TabsContent>}
        {can("zones.manage") && <TabsContent value="zones" className="mt-6"><Section><ZonesSection /></Section></TabsContent>}
        {isAdmin && <TabsContent value="taxes" className="mt-6"><Section><TaxesSection /></Section></TabsContent>}
        {isAdmin && (
          <TabsContent value="rules" className="mt-6">
            <Section><RulesSection initialCommission={stats?.commission_percent ?? 10} /></Section>
          </TabsContent>
        )}
        {isAdmin && <TabsContent value="subadmins" className="mt-6"><Section><SubAdminsSection /></Section></TabsContent>}
        {can("sessions.view") && <TabsContent value="sessions" className="mt-6"><Section><AdminSessions /></Section></TabsContent>}
        {can("performance.view") && <TabsContent value="performance" className="mt-6"><Section><AdminPerformance /></Section></TabsContent>}
        {isAdmin && <TabsContent value="data" className="mt-6"><Section><DataSection /></Section></TabsContent>}
      </Tabs>
    </div>
  );
}
